import React, { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowLeft, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import BrandLogo from "@/components/BrandLogo";
import useInterestTracking from "@/hooks/useInterestTracking";
const LabComplyxAssistant = lazy(() => import("@/components/LabComplyxAssistant"));
const CookieConsentManager = lazy(() => import("@/components/CookieConsentManager"));

const NAV_ITEMS = [
  { label: "Home", page: "Home", href: "/" },
  { label: "Solutions", page: "Solutions", href: "/solutions" },
  { label: "Pricing", page: "Pricing", href: "/pricing" },
  { label: "About", page: "About", href: "/about" },
  { label: "Contact", page: "Contact", href: "/contact" },
];



const RESOURCE_LINKS = [
  { label: "IQ OQ PQ Validation Guide", href: "/resources/iq-oq-pq-validation" },
  { label: "GxP Lab Compliance Checklist", href: "/resources/gxp-laboratory-compliance-checklist" },
  { label: "CLIA & CAP Compliance Software", href: "/resources/clia-cap-compliance-software" },
];

const TOP_LEVEL_PATHS = ["/", "/solutions", "/pricing", "/about", "/contact", "/health-score"];

export default function Layout({ children, currentPageName }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const showBack = !TOP_LEVEL_PATHS.includes(location.pathname.toLowerCase().replace(/\/$/, "") || "/");
  useInterestTracking();


  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setResourcesOpen(false);
  }, [currentPageName, location.pathname]);

  if (currentPageName === "ClientPortal") {
    return (
      <>
        {children}
        {/* The portal sidebar has its own Privacy Settings control (opens the
            same window.__openCookiePreferences hook) — suppress the floating
            pill here so it doesn't visually collide with the sidebar footer. */}
        <Suspense fallback={null}><CookieConsentManager showFloatingButton={false} /></Suspense>
      </>
    );
  }

  // ValidationWizard and QualityPlatform are portal tool pages reached only from the
  // authenticated ClientPortal sidebar -- they already mount their own NavigatorChat
  // (grounded, page_context-aware). The public marketing LabComplyxAssistant bubble
  // ("Ask LabComplyx AI") is meant for anonymous site visitors and was previously
  // rendering on top of it here too, producing two floating chat launchers stacked
  // in the same corner. Suppress only that public assistant on these two pages --
  // keep the marketing header/footer as-is since that wasn't part of the reported bug.
  const suppressPublicAssistant = currentPageName === "ValidationWizard" || currentPageName === "QualityPlatform";

  return (
    <div className="min-h-screen bg-background">
      {/* Skip-to-content link for keyboard/screen-reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-medium focus:shadow-lg"
      >
        Skip to main content
      </a>
      {/* Nav */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-brand-navy ${
          isScrolled ? "shadow-md" : ""
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-[60px]">
            <div className="flex items-center gap-2">
              {showBack && (
                <button
                  onClick={() => (window.history.length > 2 ? navigate(-1) : navigate("/"))}
                  aria-label="Go back to previous page"
                  className="p-2 -ml-2 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <Link
                to="/"
                className="flex items-center group"
                aria-label="LabComplyx home"
              >
                <BrandLogo variant="dark" />
              </Link>
            </div>

            <nav className="hidden md:flex items-center gap-0.5 lg:gap-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.page}
                  to={item.href}
                  className={`px-2.5 lg:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPageName === item.page
                      ? "text-white bg-white/10"
                      : "text-white/75 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {item.label}
                </Link>
              ))}

              <div
                className="relative"
                onMouseEnter={() => setResourcesOpen(true)}
                onMouseLeave={() => setResourcesOpen(false)}
              >
                <button
                  onClick={() => setResourcesOpen(!resourcesOpen)}
                  aria-expanded={resourcesOpen}
                  aria-haspopup="true"
                  className="flex items-center gap-1 px-2.5 lg:px-4 py-2 rounded-lg text-sm font-medium text-white/75 hover:text-white hover:bg-white/5 transition-colors"
                >
                  Resources <ChevronDown className="w-4 h-4" />
                </button>
                {resourcesOpen && (
                  <div className="absolute left-0 top-full pt-1 w-72">
                    <div className="bg-brand-navy border border-white/10 rounded-lg shadow-lg py-2">
                      {RESOURCE_LINKS.map((r) => (
                        <Link key={r.href} to={r.href} className="block px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-white/10">
                          {r.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/client-portal"
                className={`px-2.5 lg:px-4 py-2 rounded-lg text-sm font-medium border transition-colors ml-1 lg:ml-2 whitespace-nowrap ${
                  currentPageName === "ClientPortal"
                    ? "border-brand-teallight text-brand-teallight bg-white/5"
                    : "border-white/25 text-white/75 hover:text-white hover:border-white/50"
                }`}
              >
                Client Portal
              </Link>

              <Link to="/health-score">
                <Button size="sm" className="ml-1 lg:ml-2 gap-2 whitespace-nowrap bg-brand-teal hover:bg-brand-teal/90 text-white font-semibold">
                  Get Health Score
                </Button>
              </Link>
              {/* Specialized Workflows hidden from primary nav — accessible via portal/admin */}
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-brand-navy border-b border-white/10 overflow-hidden"
            >
              <div className="px-6 py-4 space-y-1">
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.page}
                    to={item.href}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      currentPageName === item.page
                        ? "text-white bg-white/10"
                        : "text-white/75 hover:text-white"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}

                <p className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-white/50">Resources</p>
                {RESOURCE_LINKS.map((r) => (
                  <Link key={r.href} to={r.href} className="block px-4 py-3 rounded-lg text-sm font-medium text-white/75 hover:text-white">
                    {r.label}
                  </Link>
                ))}

              <div className="pt-2">
                  <Link to="/health-score">
                    <Button className="w-full bg-brand-teal hover:bg-brand-teal/90 text-white font-semibold">Get Health Score</Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main */}
      <main id="main-content" tabIndex={-1}>{children}</main>
      {!suppressPublicAssistant && (
        <Suspense fallback={null}><LabComplyxAssistant /></Suspense>
      )}
      <Suspense fallback={null}><CookieConsentManager /></Suspense>

      {/* Footer */}
      <footer className="bg-brand-navy text-white" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <div className="max-w-sm">
              <p className="text-xl font-extrabold tracking-tight">
                Lab<span className="text-brand-teallight">Complyx</span>
              </p>
              <p className="mt-3 text-sm text-white/70 leading-relaxed">
                Documentation that holds up — built with your team, not around them.
              </p>
              <a href="mailto:hello@labcomplyx.com" className="mt-3 inline-block text-sm text-brand-teallight hover:underline">
                hello@labcomplyx.com
              </a>
            </div>
            <nav aria-label="Footer" className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/80">
              <Link to="/solutions" className="hover:text-white">Solutions</Link>
              <Link to="/pricing" className="hover:text-white">Pricing</Link>
              <Link to="/about" className="hover:text-white">About</Link>
              <Link to="/health-score" className="hover:text-white">Lab Health Score</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
              <Link to="/validation-intake" className="hover:text-white">Book a Call</Link>
            </nav>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6 space-y-3">
            <p className="text-xs text-white/60">
              © {new Date().getFullYear()} LabComplyx LLC. All AI outputs require human review before use as compliance documentation.
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/60">
              <Link to="/terms" className="hover:text-white">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
              <Link to="/trust-center" className="hover:text-white">Trust Center</Link>
              <button onClick={() => window.__openCookiePreferences?.()} className="hover:text-white">Privacy Settings</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}